/**
 * Wisp AI — Race-Focused MCTS
 *
 * Philosophy: The king's race to the goal row is everything.
 * Non-king pieces are expendable tools — escorts, distractions, sacrificial blockers.
 *
 * Uses standard MCTSEngine with a custom WispSimulation that biases rollouts
 * toward king advancement and a WispEvaluator that scores positions by
 * race progress rather than material advantage.
 *
 * Phase system prevents premature king rush:
 * - Opening: develop pieces first, penalize early king advancement
 * - Midgame: advance king only with escort pieces nearby
 * - Endgame: full race mode, sacrifice everything for king progress
 */

import { GameState, PieceType } from "../../types";
import { IAIPlayer, AIMoveResult, AIStats, AIPlayerType } from "../Core/IAIPlayer";
import { ZodiacEngine } from "../../zodiacEngine";
import { MCTSEngine } from "../MonteCarloAI/MCTSEngine";
import { WispEvaluator } from "./WispEvaluator";
import { WispSimulation } from "./WispSimulation";

// King selection scores — Wisp wants fast-moving kings
export const KING_RACE_SCORES: Partial<Record<PieceType, number>> = {
  [PieceType.Crane]: 95,    // 2 sq forward — best racer
  [PieceType.Penguin]: 90,  // Slide movement, very fast
  [PieceType.Goat]: 85,     // Traverse bypasses obstacles
  [PieceType.Snake]: 80,    // 2 sq orthogonal
  [PieceType.Rhino]: 75,    // 2 forward charge
  [PieceType.Frog]: 70,     // 2 diagonal + lilypad
  [PieceType.Wolf]: 60,     // Lone wolf L-move can race
  [PieceType.Bear]: 50,
  [PieceType.Monkey]: 50,
  [PieceType.Octopus]: 45,
  [PieceType.Fungi]: 40,
  [PieceType.Tree]: 35,
  [PieceType.Virus]: 30,
};

export class WispAI implements IAIPlayer {
  readonly name = AIPlayerType.WISP;
  readonly displayName = "Wisp";
  readonly description = "Race-focused MCTS that sacrifices pieces to rush its king home";

  private mctsEngine: MCTSEngine;
  private evaluator: WispEvaluator;
  private gameEngine: ZodiacEngine;
  private moveCount: number = 0;

  private stats: AIStats = {
    movesCalculated: 0,
    averageThinkingTime: 0,
    nodesEvaluated: 0,
  };
  private totalThinkingTime = 0;
  private totalNodes = 0;

  constructor(gameEngine: ZodiacEngine) {
    this.gameEngine = gameEngine;
    this.evaluator = new WispEvaluator();
    this.mctsEngine = new MCTSEngine();

    // Inject race-biased simulation into the MCTS engine
    const wispSim = new WispSimulation(this.evaluator);
    this.mctsEngine.setSimulator(wispSim);

    // Slightly lower exploration — exploit known race lines more aggressively
    this.mctsEngine.configure({
      explorationParameter: 1.2,
      useHeuristicSimulations: true,
    });
  }

  /**
   * Select king — prefer fast-moving piece types for racing.
   */
  selectKing(availableKings: PieceType[], isLightSide: boolean): PieceType {
    let bestKing = availableKings[0];
    let bestScore = -1;

    for (const king of availableKings) {
      const score = KING_RACE_SCORES[king] || 50;
      if (score > bestScore) {
        bestScore = score;
        bestKing = king;
      }
    }

    return bestKing;
  }

  async selectMove(gameState: GameState, maxThinkingTime: number): Promise<AIMoveResult> {
    const startTime = Date.now();
    this.moveCount++;

    // Pass move number to evaluator for phase detection
    this.evaluator.setMoveNumber(this.moveCount);

    // Run MCTS with race-biased simulation
    const result = this.mctsEngine.findBestMove(gameState, maxThinkingTime);

    if (!result) {
      throw new Error("Wisp AI failed to find a valid move");
    }

    const thinkingTime = Date.now() - startTime;

    // Update stats
    this.stats.movesCalculated++;
    this.totalThinkingTime += thinkingTime;
    this.totalNodes += result.explorationStats.nodesCreated;
    this.stats.averageThinkingTime = this.totalThinkingTime / this.stats.movesCalculated;
    this.stats.nodesEvaluated = this.totalNodes;

    const confidence = this.calculateConfidence(result.visits, result.winRate, result.totalIterations);

    // Include piece info for display
    const fromSquare = gameState.board[result.move.fromPosition.row]?.[result.move.fromPosition.col];
    const movingPiece = fromSquare?.Pieces.find(p => p.id === result.move.move.pieceId);

    return {
      fromPosition: result.move.fromPosition,
      move: result.move.move,
      confidence,
      thinkingTime,
      evaluation: result.winRate * 1000 - 500,
      reasoning: `Wisp (${this.getPhaseLabel()}): ${result.totalIterations} iter, ${result.visits} visits, ${(result.winRate * 100).toFixed(1)}% race win`,
      movingPiece: movingPiece ? { type: movingPiece.type, isKing: movingPiece.isKing } : undefined,
    };
  }

  getStats(): AIStats {
    return { ...this.stats };
  }

  reset(): void {
    this.moveCount = 0;
    this.stats = {
      movesCalculated: 0,
      averageThinkingTime: 0,
      nodesEvaluated: 0,
    };
    this.totalThinkingTime = 0;
    this.totalNodes = 0;
  }

  configure(config: Record<string, any>): void {
    this.mctsEngine.configure(config);
  }

  private getPhaseLabel(): string {
    if (this.moveCount <= 7) return 'opening';
    if (this.moveCount <= 16) return 'midgame';
    return 'endgame';
  }

  private calculateConfidence(visits: number, winRate: number, totalIterations: number): number {
    const visitFactor = Math.min(1.0, visits / (totalIterations * 0.3));

    let winRateFactor = 0.5;
    if (winRate > 0.8) winRateFactor = 0.9;
    else if (winRate > 0.65) winRateFactor = 0.8;
    else if (winRate > 0.55) winRateFactor = 0.7;
    else if (winRate < 0.2) winRateFactor = 0.9;
    else if (winRate < 0.35) winRateFactor = 0.8;
    else if (winRate < 0.45) winRateFactor = 0.7;

    const iterationFactor = Math.min(1.0, totalIterations / 5000) * 0.2;

    const confidence = Math.min(0.95, visitFactor * 0.5 + winRateFactor * 0.3 + iterationFactor);
    return Math.max(0.4, confidence);
  }
}
